import type { OrganizationConfig } from "../../../shared/utils/Global";
import { getStoredProgrammeType } from "../../../shared/utils/platformAuth";

export type DashboardNavbarVariant = "lender" | "platform";

export interface DashboardNavbarApiConfig {
  service: "lender_dashboard" | "platform_dashboard";
  apiPrefix: "lender" | "masterDashboard";
  defaultProgrammeType: string;
}

export const DASHBOARD_NAVBAR_CONFIG: Record<
  DashboardNavbarVariant,
  DashboardNavbarApiConfig
> = {
  lender: {
    service: "lender_dashboard",
    apiPrefix: "lender",
    defaultProgrammeType: "LAP"
  },
  platform: {
    service: "platform_dashboard",
    apiPrefix: "masterDashboard",
    defaultProgrammeType: "LAP"
  }
};

export interface ConfigNavbarUserState {
  isAuthenticated?: boolean;
  name?: string;
  email?: string;
  organizationID?: string;
  userProgramTypes?: Array<{ value: string }>;
  userData?: {
    lenderID?: string;
    organizationID?: string;
    name?: string;
    email?: string;
    userProgramTypes?: Array<{ value: string }>;
    redgateUserInfoV2?: {
      data?: {
        userAccesses?: Array<{ programType?: string }>;
      };
    };
  };
}

const getProgrammeTypeFromUserState = (
  userState: ConfigNavbarUserState
): string | undefined =>
  userState.userProgramTypes?.[0]?.value ??
  userState.userData?.userProgramTypes?.[0]?.value ??
  userState.userData?.redgateUserInfoV2?.data?.userAccesses?.[0]?.programType;

export const resolveUniqueId = (
  variant: DashboardNavbarVariant,
  userState: ConfigNavbarUserState,
  orgConfig?: OrganizationConfig
): string | undefined => {
  const isAuthenticated = userState.isAuthenticated === true;

  if (variant === "lender") {
    return isAuthenticated ? userState.userData?.lenderID : orgConfig?.lenderID;
  }

  return isAuthenticated
    ? (userState.organizationID ?? userState.userData?.organizationID)
    : orgConfig?.organizationID;
};

export const resolveProgrammeType = (
  _variant: DashboardNavbarVariant,
  userState: ConfigNavbarUserState,
  config: DashboardNavbarApiConfig,
  orgConfig: OrganizationConfig
): string => {
  const isAuthenticated = userState.isAuthenticated === true;

  if (isAuthenticated) {
    return (
      getProgrammeTypeFromUserState(userState) ??
      getStoredProgrammeType() ??
      orgConfig.metadata?.programType ??
      config.defaultProgrammeType
    );
  }

  return orgConfig.metadata?.programType ?? config.defaultProgrammeType;
};

export const resolveNavbarUser = (
  variant: DashboardNavbarVariant,
  userState: ConfigNavbarUserState
) => {
  if (userState.isAuthenticated !== true) return undefined;

  if (variant === "lender") {
    return {
      name: userState.userData?.name || userState.name,
      email: userState.userData?.email || userState.email
    };
  }

  return {
    name: userState.name || userState.userData?.name,
    email: userState.email || userState.userData?.email
  };
};

export const buildNavbarConfigPayload = (
  uniqueId: string,
  programmeType: string,
  resourceName: "layout" | "login",
  service: DashboardNavbarApiConfig["service"]
) => ({
  loanApplicationID: "",
  programType: programmeType,
  resourceName,
  service,
  sub_programme_type: "",
  unique_id: uniqueId,
  userID: ""
});
